import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  loadGoogleAnalytics,
  trackPageView,
  trackTimeOnPage,
  isAnalyticsEnabled,
} from "../../utils/analytics";

/**
 * Tracks page views on every route change (SPA) and time on page when the user
 * leaves (tab hidden or navigate away). Renders nothing.
 */
const AnalyticsTracker: React.FC = () => {
  const location = useLocation();
  const pageStartedAt = useRef<number>(Date.now());
  const currentPath = useRef<string>(location.pathname + location.search);

  // Load GA once when enabled
  useEffect(() => {
    if (isAnalyticsEnabled()) {
      loadGoogleAnalytics();
    }
  }, []);

  // On route change: send time on previous page (if we navigated), then track new page view
  useEffect(() => {
    const path = location.pathname + location.search;

    if (isAnalyticsEnabled()) {
      const now = Date.now();
      const previousPath = currentPath.current;

      if (previousPath !== path) {
        const timeSpentSeconds = (now - pageStartedAt.current) / 1000;
        trackTimeOnPage(previousPath, timeSpentSeconds);
      }

      currentPath.current = path;
      pageStartedAt.current = now;
      trackPageView(path, document.title);
    }
  }, [location.pathname, location.search]);

  // On tab close or navigate away (e.g. external link): send time on page
  useEffect(() => {
    if (!isAnalyticsEnabled()) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        const timeSpentSeconds = (Date.now() - pageStartedAt.current) / 1000;
        trackTimeOnPage(currentPath.current, timeSpentSeconds);
      } else if (document.visibilityState === "visible") {
        pageStartedAt.current = Date.now();
      }
    };

    const handleBeforeUnload = () => {
      const timeSpentSeconds = (Date.now() - pageStartedAt.current) / 1000;
      trackTimeOnPage(currentPath.current, timeSpentSeconds);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return null;
};

export default AnalyticsTracker;
